// ============================================================
// Pay Rocky - UI Primitives
// Reusable base components used throughout the app
// ============================================================

import React from 'react';
import { Loader2 } from 'lucide-react';

/* Button */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  className = '',
  ...props
}) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    borderRadius: 'var(--radius-md)',
    border: '1px solid transparent',
    boxShadow: '0 10px 24px rgba(105, 61, 43, 0.08)',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.55 : 1,
    transition: 'all 0.18s ease',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
  };

  const sizes = {
    sm: { padding: '7px 14px', fontSize: '12px' },
    md: { padding: '10px 20px', fontSize: '14px' },
    lg: { padding: '13px 28px', fontSize: '15px' },
  };

  const variants = {
    primary: {
      background: 'linear-gradient(135deg, var(--clr-accent) 0%, var(--clr-accent-2) 100%)',
      color: 'var(--clr-on-accent)',
      borderColor: 'var(--clr-accent)',
    },
    ghost: {
      background: 'var(--clr-surface)',
      color: 'var(--clr-text)',
      borderColor: 'var(--clr-border-md)',
      boxShadow: 'none',
    },
    danger: {
      background: 'var(--clr-danger-dim)',
      color: 'var(--clr-danger)',
      borderColor: 'rgba(176, 58, 48, 0.22)',
      boxShadow: 'none',
    },
    outline: {
      background: 'transparent',
      color: 'var(--clr-accent)',
      borderColor: 'var(--clr-accent)',
      boxShadow: 'none',
    },
    success: {
      background: 'var(--clr-accent-dim)',
      color: 'var(--clr-accent)',
      borderColor: 'var(--clr-accent-glow)',
      boxShadow: 'none',
    },
  };

  return (
    <button
      data-full-width={fullWidth ? 'true' : undefined}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} />}
      {children}
    </button>
  );
}

/* Badge */
export function Badge({ children, status }) {
  const statusMap = {
    PENDING: { color: 'var(--clr-warn)', bg: 'var(--clr-warn-dim)' },
    PROCESSING: { color: 'var(--clr-info)', bg: 'var(--clr-info-dim)' },
    COMPLETED: { color: 'var(--clr-accent)', bg: 'var(--clr-accent-dim)' },
    CANCELLED: { color: 'var(--clr-danger)', bg: 'var(--clr-danger-dim)' },
    verified: { color: 'var(--clr-accent)', bg: 'var(--clr-accent-dim)' },
    pending: { color: 'var(--clr-warn)', bg: 'var(--clr-warn-dim)' },
    unverified: { color: 'var(--clr-danger)', bg: 'var(--clr-danger-dim)' },
    rejected: { color: 'var(--clr-danger)', bg: 'var(--clr-danger-dim)' },
    suspended: { color: 'var(--clr-danger)', bg: 'var(--clr-danger-dim)' },
    buy: { color: 'var(--clr-accent)', bg: 'var(--clr-accent-dim)' },
    sell: { color: 'var(--clr-danger)', bg: 'var(--clr-danger-dim)' },
  };

  const style = statusMap[status] || { color: 'var(--clr-text-2)', bg: 'var(--clr-surface-2)' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: '99px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: style.color,
        background: style.bg,
      }}
    >
      {children}
    </span>
  );
}

/* Card */
export function Card({ children, style = {}, className = '', ...props }) {
  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.92)',
        border: '1px solid var(--clr-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.25rem',
        boxShadow: '0 18px 50px rgba(105, 61, 43, 0.08)',
        ...style,
      }}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
}

/* MetricCard */
export function MetricCard({ label, value, accent = false, icon }) {
  return (
    <div
      style={{
        background: accent ? 'var(--clr-accent-dim)' : 'var(--clr-surface)',
        border: `1px solid ${accent ? 'var(--clr-accent-glow)' : 'var(--clr-border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: '1rem',
      }}
    >
      <div style={{ fontSize: '11px', color: accent ? 'var(--clr-accent)' : 'var(--clr-text-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {icon && icon}
        {label}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: 'var(--font-display)', color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>
        {value}
      </div>
    </div>
  );
}

/* FormField */
export function FormField({ label, error, children, hint }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--clr-text-2)', marginBottom: '6px', letterSpacing: '0.02em' }}>
          {label}
        </label>
      )}
      {children}
      {hint && !error && (
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginTop: '5px' }}>{hint}</div>
      )}
      {error && (
        <div style={{ fontSize: '11px', color: 'var(--clr-danger)', marginTop: '5px' }}>{error}</div>
      )}
    </div>
  );
}

/* TextInput */
export function TextInput({ error, ...props }) {
  return (
    <input
      {...props}
      style={{
        width: '100%',
        padding: '10px 14px',
        background: 'var(--clr-surface)',
        border: `1px solid ${error ? 'var(--clr-danger)' : 'var(--clr-border-md)'}`,
        borderRadius: 'var(--radius-md)',
        color: 'var(--clr-text)',
        fontSize: '14px',
        transition: 'border-color 0.15s',
        ...props.style,
      }}
    />
  );
}

/* SelectInput */
export function SelectInput({ children, error, ...props }) {
  return (
    <select
      {...props}
      style={{
        width: '100%',
        padding: '10px 14px',
        background: 'var(--clr-surface)',
        border: `1px solid ${error ? 'var(--clr-danger)' : 'var(--clr-border-md)'}`,
        borderRadius: 'var(--radius-md)',
        color: 'var(--clr-text)',
        fontSize: '14px',
        cursor: 'pointer',
        ...props.style,
      }}
    >
      {children}
    </select>
  );
}

/* Divider */
export function Divider({ style = {} }) {
  return <div style={{ height: '1px', background: 'var(--clr-border)', margin: '1rem 0', ...style }} />;
}

/* Spinner */
export function Spinner({ size = 20 }) {
  return (
    <Loader2
      size={size}
      style={{
        color: 'var(--clr-accent)',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}

/* Skeleton */
export function Skeleton({ width = '100%', height = '14px', radius = '10px', style = {} }) {
  return (
    <div
      className="payrocky-skeleton"
      style={{
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, rgba(239, 227, 218, 0.7) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(239, 227, 218, 0.7) 100%)',
        backgroundSize: '220% 100%',
        animation: 'payrocky-skeleton-wave 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

/* LoadingCard */
export function LoadingCard({ lines = 3, titleWidth = '40%' }) {
  return (
    <Card>
      <div style={{ display: 'grid', gap: '10px' }}>
        <Skeleton width={titleWidth} height="16px" />
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton key={index} width={index === lines - 1 ? '72%' : '100%'} height="12px" />
        ))}
      </div>
    </Card>
  );
}

/* EmptyState */
export function EmptyState({ icon, title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      {icon && <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>{icon}</div>}
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>{title}</div>
      {description && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '20px' }}>{description}</div>}
      {action}
    </div>
  );
}

if (typeof document !== 'undefined' && !document.getElementById('vaultx-spin-style')) {
  const style = document.createElement('style');
  style.id = 'vaultx-spin-style';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } } @keyframes payrocky-skeleton-wave { 0% { background-position: 200% 0; } 100% { background-position: -20% 0; } }';
  document.head.appendChild(style);
}
